import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Sparkles, UploadCloud } from "lucide-react";

export default function AdminCategories() {
  const [categories, setCategories] = useState(null);

  const load = () => api.get("/admin/categories").then((res) => setCategories(res.data));
  useEffect(() => { load(); }, []);

  const publish = async (slug) => {
    try {
      const { data } = await api.post(`/admin/wordpress/publish-category/${slug}`);
      toast.success(`Published: ${data.link}`);
      load();
    } catch {
      toast.error("Publish failed");
    }
  };

  return (
    <div data-testid="admin-categories-page">
      <h1 className="text-2xl font-serif-heading font-bold text-navy mb-2">Categories</h1>
      <p className="text-sm text-slate-500 mb-6">Service pillar hubs get deep AI-drafted editorial content that anchors each vertical for SEO.</p>
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Category</TableHead><TableHead>Pillar</TableHead>
              <TableHead>Pillar Content</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {categories?.map((c) => (
              <TableRow key={c.slug} data-testid={`admin-category-row-${c.slug}`}>
                <TableCell className="font-medium text-navy">{c.name}</TableCell>
                <TableCell>{c.pillar ? <Badge className="bg-navy text-white">Pillar</Badge> : <Badge variant="outline">General</Badge>}</TableCell>
                <TableCell>{c.pillar_content_html ? <Badge className="bg-green-100 text-green-700">Drafted</Badge> : <Badge variant="outline">None</Badge>}</TableCell>
                <TableCell className="text-right space-x-2">
                  {c.pillar && <PillarContentDialog category={c} onSaved={load} />}
                  <Button size="sm" variant="outline" onClick={() => publish(c.slug)} data-testid={`admin-category-publish-button-${c.slug}`}>
                    <UploadCloud className="w-3.5 h-3.5 mr-1" /> Publish
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function PillarContentDialog({ category, onSaved }) {
  const [open, setOpen] = useState(false);
  const [html, setHtml] = useState(category.pillar_content_html || "");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/admin/categories/${category.slug}/generate-pillar-content`);
      setHtml(data.pillar_content_html);
      toast.success("Draft generated — review and edit before saving");
    } catch {
      toast.error("AI generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/categories/${category.slug}`, { pillar_content_html: html });
      toast.success("Saved. Click Publish to push it live.");
      onSaved();
      setOpen(false);
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`admin-category-pillar-content-button-${category.slug}`}>
          <Sparkles className="w-3.5 h-3.5 mr-1" /> Pillar Content
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid={`admin-category-pillar-dialog-${category.slug}`}>
        <DialogHeader>
          <DialogTitle>{category.name} — Pillar Hub Content</DialogTitle>
          <DialogDescription>Generate deep AI-drafted editorial content for this pillar hub, review/edit it, then save.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Button onClick={generate} disabled={generating} variant="outline" className="w-full" data-testid="pillar-content-generate-button">
            <Sparkles className="w-4 h-4 mr-2" /> {generating ? "Generating..." : html ? "Regenerate with AI" : "Generate with AI"}
          </Button>
          <Textarea
            value={html}
            onChange={(e) => setHtml(e.target.value)}
            rows={16}
            placeholder="AI-generated pillar content HTML will appear here for review/editing before saving."
            className="font-mono text-xs"
            data-testid="pillar-content-textarea"
          />
          <Button onClick={save} disabled={saving || !html} className="w-full bg-gold hover:bg-gold-600 text-navy font-bold" data-testid="pillar-content-save-button">
            {saving ? "Saving..." : "Save Draft"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
